/**
 * MapWithFilter Component
 *
 * Full-width map with a collapsible filter panel overlay.
 * Filter bar above the map toggles the filter panel.
 *
 * FEATURES:
 * - Full-width map that fills the container
 * - Filter bar above the map with toggle functionality
 * - Filter panel overlays the map from the right side
 * - Scrollable filter panel if content overflows
 * - Responsive design with mobile support
 *
 * USAGE:
 * <MapWithFilter
 *   markers={contractorMarkers}
 *   onFilterChange={handleFilterChange}
 *   showFilter={true}
 * />
 */

'use client';

import { useState, type HTMLAttributes, memo, useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { cn } from '@/src/shared/lib/utils';
import { MapFilterPanel, type FilterState } from '@/src/widgets/filters/MapFilterPanel';

// Dynamic import for Leaflet to avoid SSR issues
const LeafletMapComponent = dynamic(() => import('@/src/widgets/map/LeafletMap').then((mod) => mod.LeafletMap), {
  ssr: false,
  loading: () => <div className="flex items-center justify-center h-full bg-gray-100">Loading map...</div>,
});

// Memoized wrapper to prevent unnecessary re-renders
const LeafletMap = memo(LeafletMapComponent, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.markers === nextProps.markers &&
    prevProps.center?.lat === nextProps.center?.lat &&
    prevProps.center?.lng === nextProps.center?.lng &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.selectedMarkerId === nextProps.selectedMarkerId &&
    prevProps.height === nextProps.height
  );
});

interface Marker {
  id: string;
  lat: number;
  lng: number;
  contractorName?: string;
  avatarUrl?: string | null;
  rating?: number | null;
}

export interface MapWithFilterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * Array of map markers to display
   * Each marker represents a contractor location
   */
  markers?: Marker[];

  /**
   * Center position for the map
   * @default { lat: 43.6532, lng: -79.3832 } (Toronto)
   */
  center?: { lat: number; lng: number };

  /**
   * Initial zoom level for the map
   * @default 12
   */
  zoom?: number;

  /**
   * Callback function when filter values change
   * Receives the complete filter state
   */
  onFilterChange?: (filters: FilterState) => void;

  /**
   * Whether to show the filter toggle
   * Set to false to hide filter functionality
   *
   * @default true
   */
  showFilter?: boolean;

  /**
   * Height for the map container
   * Accepts any valid CSS height value
   *
   * @default '500px'
   */
  mapHeight?: string;

  /**
   * Custom classes for the main container
   */
  containerClassName?: string;

  /**
   * Custom classes for the map container
   */
  mapClassName?: string;

  /**
   * Number of active filters to display in badge
   */
  activeFilterCount?: number;

  /**
   * User's location to display on map
   */
  userLocation?: { lat: number; lng: number } | null;

  /**
   * Initial filter values (e.g., location from profile)
   */
  initialFilters?: Partial<FilterState>;

  /**
   * ID of the currently selected marker to highlight
   */
  selectedMarkerId?: string | null;
}

/**
 * MapWithFilter Component
 *
 * Full-width map with collapsible filter overlay panel.
 */
export const MapWithFilter: React.FC<MapWithFilterProps> = ({
  markers = [],
  center,
  zoom,
  onFilterChange,
  showFilter = true,
  mapHeight = '500px',
  containerClassName,
  mapClassName,
  activeFilterCount = 0,
  userLocation,
  initialFilters,
  selectedMarkerId,
  className,
  ...props
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const renderCountRef = useRef(0);

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Log re-renders and check container height
  useEffect(() => {
    renderCountRef.current += 1;
    console.log('[MapWithFilter] Component rendered, count:', renderCountRef.current);

    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      console.log('[MapWithFilter] Map container actual dimensions:', {
        height: rect.height,
        width: rect.width,
        top: rect.top,
        bottom: rect.bottom,
      });
      console.log('[MapWithFilter] Map container computed style:', {
        height: window.getComputedStyle(mapContainerRef.current).height,
        minHeight: window.getComputedStyle(mapContainerRef.current).minHeight,
        maxHeight: window.getComputedStyle(mapContainerRef.current).maxHeight,
      });
    }
  });

  // Wrap filter change to prevent re-renders of map
  const handleFilterChange = useCallback((filters: FilterState) => {
    console.log('[MapWithFilter] handleFilterChange called with:', filters);
    console.log('[MapWithFilter] Map container height before update:', mapHeight);

    // Delay the callback to next tick to prevent layout issues
    requestAnimationFrame(() => {
      console.log('[MapWithFilter] Calling parent onFilterChange in requestAnimationFrame');
      onFilterChange?.(filters);
    });
  }, [onFilterChange, mapHeight]);

  return (
    <div
      className={cn('w-full', containerClassName, className)}
      {...props}
    >
      {/* Filter Bar */}
      {showFilter && (
        <div className="h-12 bg-background-secondary flex items-center justify-end px-4 relative z-20 mb-0">
          <button
            onClick={toggleFilter}
            className="inline-flex items-center gap-2 py-2.5 rounded-full hover:bg-black/5 transition-colors px-3 cursor-pointer"
            aria-expanded={isFilterOpen}
            aria-controls="filter-panel"
            type="button"
          >
            <span className="text-text-primary text-xl font-medium font-['Roboto'] leading-6">
              Filters
            </span>
            {activeFilterCount > 0 && (
              <span className="h-6 min-w-6 px-1 bg-feedback-error rounded-full inline-flex items-center justify-center">
                <span className="text-text-inverse text-base font-extrabold font-['Roboto'] leading-5">
                  {activeFilterCount}
                </span>
              </span>
            )}
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              className={cn(
                'transition-transform duration-200',
                isFilterOpen ? '' : 'rotate-180'
              )}
            >
              <path
                d="M10 18L16 12L22 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-text-primary"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Map and Filter Container - Side by Side */}
      <div className="relative w-full" style={{ height: mapHeight }}>
        {/* Map Container - Fixed Height */}
        <div
          ref={mapContainerRef}
          className="w-full"
          style={{
            height: mapHeight,
            minHeight: mapHeight,
            maxHeight: mapHeight,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <div
            className={cn('w-full overflow-hidden', mapClassName)}
            style={{
              height: mapHeight,
              minHeight: mapHeight,
              maxHeight: mapHeight,
            }}
          >
            <LeafletMap
              markers={markers}
              center={center}
              zoom={zoom}
              rounded={false}
              userLocation={userLocation}
              height={mapHeight}
              selectedMarkerId={selectedMarkerId}
            />
          </div>
        </div>

        {/* Filter Panel - OUTSIDE map container, positioned absolutely */}
        {showFilter && isFilterOpen && (
          <div
            id="filter-panel"
            className="absolute top-0 right-0 w-full sm:w-80 bg-background-primary shadow-lg transition-opacity duration-300 ease-in-out"
            style={{
              zIndex: 10000,
              height: mapHeight,
              maxHeight: mapHeight,
            }}
          >
            <MapFilterPanel
              onFilterChange={handleFilterChange}
              className="h-full overflow-y-auto"
              initialFilters={initialFilters}
            />
          </div>
        )}
      </div>
    </div>
  );
};

MapWithFilter.displayName = 'MapWithFilter';

// Export types for external use
export type { Marker, FilterState };
